import { useEffect, useRef, type ReactNode } from 'react';
import { ModalPage, type ModalPageProps } from '@vkontakte/vkui';
import { DragHandle } from './DragHandle';
import { useSheetHeaderDrag } from './useSheetDrag';

/**
 * Unique ModalPage id per open.
 * VKUI caches snap-points by id and mutates `initial` after each drag,
 * which deadlocks pan on reopen. A fresh id = fresh cache entry.
 */
export function sheetId(base: string, openToken: number): string {
  return `${base}__${openToken}`;
}

/**
 * ModalPage with drag-resize.
 *
 * Drag zone = full header (gray handle + ModalPageHeader title area).
 * Pan starts immediately on pointer down — no long-press.
 * Drag down to close.
 */
export function ResizableModalPage({
  id,
  openToken,
  children,
  settlingHeight = 80,
  onClose,
  ...rest
}: Omit<ModalPageProps, 'children' | 'id'> & {
  id: string;
  openToken: number;
  children: ReactNode;
}) {
  const resolvedId = sheetId(id, openToken);
  const rootRef = useRef<HTMLDivElement>(null);

  useSheetHeaderDrag(rootRef, {
    active: true,
    onClose: () => onClose?.('swipe-down' as never),
    onExpand: () => {
      /* size is controlled by settlingHeight */
    },
  });

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const t = window.setTimeout(() => {
      root
        .querySelectorAll<HTMLElement>(
          '[class*="ModalPageHeader"], [class*="ModalPage__header"], [class*="modal-page__header"]',
        )
        .forEach((h) => {
          h.style.touchAction = 'none';
          h.style.cursor = 'grab';
          h.style.userSelect = 'none';
        });
    }, 20);
    return () => window.clearTimeout(t);
  }, [resolvedId]);

  return (
    <ModalPage
      key={resolvedId}
      id={resolvedId}
      settlingHeight={settlingHeight}
      onClose={onClose}
      {...rest}
    >
      <div ref={rootRef} style={{ display: 'contents' }}>
        <DragHandle />
        {children}
      </div>
    </ModalPage>
  );
}
